import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { AuditItem } from 'src/api/types';
import { GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import _ from 'lodash';
import { PlainUserLink, TableUserItem, TextOverflowTooltip } from 'src/common';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { genEntityJsx, getEntityElements } from './util';
import { Link } from 'react-router-dom';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { EventCompletionStatus, useDeleteEventMutation } from 'src/graphql/_generated';
import { formatDate } from 'src/utils/misc';

type UseColumns = (a: { data?: AuditItem[]; tableId: TableIds; refetch?: () => void; showAudit?: boolean }) => {
  columns: ColumnDef<AuditItem>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['user_name', 'object', 'name', 'action', 'day'];

const parseAssignedUser = (assignedValue: string | null | undefined) => {
  if (!assignedValue) return { name: '', email: '' };

  const emailMatch = assignedValue.match(/^(.+?)\s*\(([^)]+@[^)]+)\)$/);
  if (emailMatch) {
    return {
      name: emailMatch[1].trim(),
      email: emailMatch[2].trim(),
    };
  }

  return {
    name: assignedValue,
    email: '',
  };
};

export const useAuditColumns: UseColumns = ({ data = [], tableId, refetch, showAudit }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${tableId}-columns`, defaultColumns);
  const [deleteEvent] = useDeleteEventMutation({ onCompleted: refetch });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    const list: ColumnDef<AuditItem>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'user_name',
        header: 'User',
        accessorFn: (row) => row.user_name || null,
        cell: ({ row }) => (
          <TableUserItem
            name={row.original.user_name}
            profilePhotoPath={row.original.profile_photo_path}
            interactive={{
              scope: 'workspace',
              email: row.original.user_email,
            }}
          />
        ),
        sortingFn: sortingFns.text,
        enableSorting: false,
        size: 100,
      },
      {
        id: 'object',
        header: 'Object',
        accessorFn: (row) => row.entity || null,
        cell: ({ row }) => getEntityElements(row.original.entity)?.typeName,
        enableSorting: false,
        size: 100,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name || null,
        cell: ({ row }) => {
          const elements = getEntityElements(row.original.entity);
          const renderName = elements?.name || row.original.name;

          if (row.original.entity.type === 'member') {
            const emailMatch = row.original.entity.name?.match(/^([^\s]+@[^\s]+)/);
            const email = emailMatch?.[1] || '';
            return (
              <TextOverflowTooltip tooltipText={row.original.entity.name || ''}>
                <PlainUserLink
                  email={email}
                  name={row.original.entity.name || row.original.name}
                  userId={row.original.entity.id}
                />
              </TextOverflowTooltip>
            );
          }

          return (
            <TextOverflowTooltip tooltipText={row.original.entity.name || ''}>
              {elements?.path ? (
                <Link
                  className="table-link"
                  to={elements.path}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {renderName}
                </Link>
              ) : (
                renderName
              )}
            </TextOverflowTooltip>
          );
        },
        enableSorting: false,
        size: 100,
      },
      {
        id: 'action',
        header: showAudit ? 'Action' : 'Event',
        accessorFn: (row) => row.action_title || null,
        cell: ({ row }) => {
          let renderValue: string | React.ReactNode = row.original.action_title;

          if (
            (row.original.types === 'event_Action-I' || row.original.types === 'event_Action-U') &&
            row.original.actions
          ) {
            const actions = row.original.actions;
            const changedActions = actions.filter((action) => action.before !== action.after);

            const completionStatusAction = actions.find((action) => action.field_name === 'Completion Status');
            if (
              completionStatusAction &&
              completionStatusAction.before !== completionStatusAction.after &&
              completionStatusAction.before !== null
            ) {
              if (
                completionStatusAction.after === 'Complete' ||
                completionStatusAction.after === EventCompletionStatus.Done
              ) {
                renderValue = 'Action was marked as Complete';
              } else if (
                completionStatusAction.before === 'Complete' ||
                completionStatusAction.before === EventCompletionStatus.Done
              ) {
                renderValue = 'Action was reopened';
              }
            } else if (row.original.action_title === 'Created') {
              const notesAction = actions.find((action) => action.field_name === 'Title');
              const actionTitle = notesAction?.after || row.original.entity?.name || 'Action';
              renderValue = `Action Created: ${actionTitle}`;
            } else if (changedActions.length > 0) {
              if (changedActions.length === 1) {
                const changedAction = changedActions[0];

                switch (changedAction.field_name) {
                  case 'Owner':
                    const { name, email } = parseAssignedUser(String(changedAction.after || ''));
                    renderValue = (
                      <>
                        Action Updated: Assigned to <PlainUserLink name={name} email={email} />
                      </>
                    );
                    break;
                  case 'Due Date':
                    const dueDate = String(changedAction.after);
                    renderValue = (
                      <>
                        Action Updated: Due Date to <TLLink date={dueDate}>{dueDate}</TLLink>
                      </>
                    );
                    break;
                  case 'Completion Status':
                    renderValue = `Action Updated: Completion Status changed to ${changedAction.after}`;
                    break;
                  case 'Date Completed':
                    const completedDate = String(changedAction.after);
                    renderValue = (
                      <>
                        Action Updated: Date Completed to <TLLink date={completedDate}>{completedDate}</TLLink>
                      </>
                    );
                    break;
                  case 'Date Created':
                    const createdDate = String(changedAction.after);

                    renderValue = (
                      <>
                        Action Updated: Date Created to <TLLink date={createdDate}>{createdDate}</TLLink>
                      </>
                    );
                    break;
                  case 'Title':
                    renderValue = `Action Updated: Title changed to ${changedAction.after}`;
                    break;
                  default:
                    renderValue = `Action Updated: ${changedAction.field_name} changed to ${changedAction.after}`;
                }
              } else {
                const fieldNames = changedActions.map((action) => {
                  switch (action.field_name) {
                    case 'Title':
                      return 'Title';
                    case 'Due Date':
                      return 'Due Date';
                    case 'Owner':
                      return 'Owner';
                    case 'Completion Status':
                      return 'Status';
                    case 'Date Completed':
                      return 'Date Completed';
                    case 'Date Created':
                      return 'Date Created';
                    default:
                      return action.field_name;
                  }
                });

                if (fieldNames.length === 2) {
                  renderValue = `Action Updated: ${fieldNames.join(' and ')} Changed`;
                } else {
                  renderValue = `Action Updated: ${fieldNames.slice(0, -1).join(', ')} and ${fieldNames[fieldNames.length - 1]} Changed`;
                }
              }
            }
          }

          if (row.original.types === 'event_Comment-I') {
            renderValue = `Added comment: ${renderValue}`;
          }

          const elements = getEntityElements(row.original.entity_action);
          const entityName = row.original.entity_action?.name || undefined;
          const hasId = !!row.original.entity_action?.id;

          if (typeof renderValue !== 'string') {
            return renderValue;
          }

          return (
            <TextOverflowTooltip tooltipText={renderValue || ''}>
              {genEntityJsx({
                renderName: renderValue,
                entityName,
                isDeleted: elements?.isDeleted,
                path: hasId ? elements?.path : undefined,
              })}
            </TextOverflowTooltip>
          );
        },
        enableSorting: false,
        size: 200,
      },

      {
        id: 'day',
        header: 'Date',
        accessorFn: (row) => row.date_time || null,
        cell: ({ row }) => {
          const date = row.original.date_time;
          return <TLLink date={date.split(',')[0].split('-').reverse().join('-')}>{date}</TLLink>;
        },
        size: 100,
      },

      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const canDelete = !showAudit && row.original.types === 'event_Comment-I' && row.original.entity.status === 'available';

          const onDeleteClick = canDelete
            ? () => {
                deleteEvent({
                  variables: { id: row.original.entity_action?.id! },
                });
              }
            : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm onDeleteClick={onDeleteClick} />
            </div>
          );
        },
        size: 1,
        maxSize: 20,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, showAudit]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={tableId}
      options={[
        { key: 'user_name', title: 'User' },
        { key: 'object', title: 'Object' },
        { key: 'name', title: 'Name' },
        { key: 'action', title: showAudit ? 'Action' : 'Event' },
        { key: 'day', title: 'Date' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
